/** SUNTRONIX ONE — Signature canvas.
 *
 *  Small, dependency-free React signature pad. Captures pointer + touch
 *  events, renders anti-aliased strokes on a `<canvas>`, exposes an
 *  imperative API via a forwarded ref so parents can `.clear()` and
 *  `.toDataURL()` from a submit button.
 *
 *  The canvas backing store is sized against `devicePixelRatio` so
 *  strokes stay crisp on Retina / hi-DPI screens; the CSS box stays a
 *  well-behaved responsive rectangle.
 */
import {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from "react";

const SignaturePad = forwardRef(function SignaturePad(
  {
    height = 160,
    strokeStyle = "#0f172a",
    strokeWidth = 2,
    backgroundStyle = "#ffffff",
    className = "",
    onDirtyChange,
  },
  ref,
) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const dirtyRef = useRef(false);
  const [empty, setEmpty] = useState(true);

  const setDirty = useCallback((v) => {
    dirtyRef.current = v;
    setEmpty(!v);
    onDirtyChange?.(v);
  }, [onDirtyChange]);

  // ----- Sizing (devicePixelRatio-aware) -----
  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = backgroundStyle;
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = strokeStyle;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = strokeWidth;
  }, [backgroundStyle, strokeStyle, strokeWidth]);

  useEffect(() => {
    resize();
    const onResize = () => resize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [resize]);

  // ----- Coordinates helper -----
  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const beginStroke = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };
  const extendStroke = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pointFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!dirtyRef.current) setDirty(true);
  };
  const endStroke = () => { drawingRef.current = false; lastPointRef.current = null; };

  // ----- Imperative API -----
  useImperativeHandle(ref, () => ({
    clear() {
      resize();
      setDirty(false);
    },
    isEmpty() { return !dirtyRef.current; },
    toDataURL(type = "image/png") {
      // Trim excess whitespace so the stored PNG is tight.
      const src = canvasRef.current;
      try {
        const ctx = src.getContext("2d");
        const { width, height: h } = src;
        const img = ctx.getImageData(0, 0, width, h);
        let minX = width, minY = h, maxX = 0, maxY = 0;
        const data = img.data;
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < width; x++) {
            const idx = (y * width + x) * 4;
            const r = data[idx], g = data[idx + 1], b = data[idx + 2];
            // any non-white pixel counts as ink
            if (r < 250 || g < 250 || b < 250) {
              if (x < minX) minX = x;
              if (y < minY) minY = y;
              if (x > maxX) maxX = x;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX <= minX || maxY <= minY) return src.toDataURL(type);
        const pad = 10;
        minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
        maxX = Math.min(width, maxX + pad); maxY = Math.min(h, maxY + pad);
        const w = maxX - minX, hh = maxY - minY;
        const off = document.createElement("canvas");
        off.width = w; off.height = hh;
        off.getContext("2d").drawImage(src, minX, minY, w, hh, 0, 0, w, hh);
        return off.toDataURL(type);
      } catch {
        return src.toDataURL(type);
      }
    },
  }), [resize, setDirty]);

  return (
    <div className={"relative " + className}>
      <canvas
        ref={canvasRef}
        className="w-full border rounded bg-white touch-none cursor-crosshair"
        style={{ height }}
        onMouseDown={beginStroke}
        onMouseMove={extendStroke}
        onMouseUp={endStroke}
        onMouseLeave={endStroke}
        onTouchStart={beginStroke}
        onTouchMove={extendStroke}
        onTouchEnd={endStroke}
        data-testid="signature-canvas"
      />
      {empty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-slate-300 text-[13px] italic">
          Sign here
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
